import axios from 'axios';
import { BASE_URL } from '../config';

/** API Class.
 *
 * Static class tying together methods used to get/send to to the API.
 * There shouldn't be any frontend-specific stuff here, and there shouldn't
 * be any API-aware stuff elsewhere in the frontend....
 *
 */

// Create a named Axios instance with interceptors for automatic token management
export const apiClient = axios.create();

// Lazy import to avoid circular dependency (Api.js <-> store.js)
// Safe because interceptor only executes at request time, not import time
let _store = null;
const getStore = () => {
	if (!_store) {
		_store = require('../store').store;
	}
	return _store;
};

// Re-entrancy flag: prevents infinite 401 loops when stopImpersonation
// dispatches an API call that itself returns 401.
let _handling401 = false;

/** Token precedence for requests WITHOUT an explicit Authorization header:
 *   admin endpoint → admin token (even during impersonation)
 *   impersonating  → impersonated user's token
 *   else           → active account's token (`user.token` is its projection),
 *                    falling back to the admin token.
 * An explicit header always wins and never reaches this function. */
export const selectAuthToken = (state, url = '') => {
	const adminState = state?.admin || {};
	const userState = state?.user || {};
	const u = url || '';
	if (u.includes('admins/') || u.includes('api/admin/')) {
		return adminState.originalAdminToken || adminState.token || null;
	}
	if (adminState.isImpersonating && adminState.impersonatedToken) {
		return adminState.impersonatedToken;
	}
	return userState.token || adminState.token || null;
};

const bearerOf = (authHeader) => {
	if (typeof authHeader !== 'string') return null;
	const m = authHeader.match(/^Bearer\s+(.+)$/);
	return m ? m[1] : null;
};

/** Decide what a 401 on a plain-user request should do (#168).
 * Maps the failing request's Bearer token back to a signed-in account:
 *  - no accounts at all             → 'logoutAll' (today's behavior)
 *  - token matches the only account → 'logoutAll'
 *  - token matches one of several   → 'evict' that account only
 *  - token matches no account       → 'ignore' (stale token; don't sign anyone out) */
export const resolve401Target = (userState, authHeader) => {
	const accounts = Array.isArray(userState?.accounts) ? userState.accounts : [];
	if (accounts.length === 0) return { action: 'logoutAll' };
	const bearer = bearerOf(authHeader);
	const match = bearer ? accounts.find((a) => a.token === bearer) : null;
	if (!match) return { action: 'ignore' };
	if (accounts.length === 1) return { action: 'logoutAll' };
	return {
		action: 'evict',
		username: match.username,
		wasActive: match.username === userState.activeUsername,
	};
};

// Persist writes are async; flush before a hard navigation so the change sticks.
const flushPersist = async () => {
	try {
		const { persistor } = require('../store');
		if (persistor?.flush) await persistor.flush();
	} catch (err) {
		console.debug('401 interceptor: persist flush failed', err?.message);
	}
};

// Request interceptor: automatically attach the correct token from Redux state
apiClient.interceptors.request.use(
	(config) => {
		// Only set Authorization if not already provided (explicit headers take priority)
		if (config.headers?.Authorization) {
			return config;
		}

		try {
			const token = selectAuthToken(getStore().getState(), config.url);

			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}
		} catch (err) {
			// Store may not be initialized yet (e.g., during testing)
			console.debug('Token interceptor: store not available', err.message);
		}

		return config;
	},
	(error) => Promise.reject(error)
);

// Response interceptor: handle 401 errors by dispatching logout and redirecting
apiClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		if (error.response?.status === 401) {
			// Skip 401 handling for the end-impersonation endpoint to avoid recursive loops
			const requestUrl = error.config?.url || '';
			if (requestUrl.includes('admins/impersonate/end')) {
				return Promise.reject(error);
			}
			// Skip 401 handling for login endpoints — failed login isn't an expired session, and logout/redirect would wipe the error message
			if (requestUrl.endsWith('/auth/userToken') || requestUrl.endsWith('/auth/adminToken')) {
				return Promise.reject(error);
			}

			// Guard against re-entrancy: stopImpersonation dispatches an API call that
			// may itself 401, which would trigger this interceptor again.
			if (_handling401) {
				return Promise.reject(error);
			}

			_handling401 = true;
			try {
				const store = getStore();
				const state = store.getState();
				const adminState = state.admin || {};

				// Lazy import to avoid circular dependency (see getStore comment above)
				if (adminState.isImpersonating) {
					const { stopImpersonation, IMPERSONATION_END_REASONS } = require('../store/admin');
					await store.dispatch(stopImpersonation(IMPERSONATION_END_REASONS.EXPIRED));
					window.location.href = '/admin/clients';
				} else if (adminState.token) {
					const { adminLogout } = require('../store/admin');
					store.dispatch(adminLogout());
					window.location.href = '/admin/login';
				} else {
					const { userLogout, removeAccount } = require('../store/user');
					const target = resolve401Target(state.user, error.config?.headers?.Authorization);
					if (target.action === 'evict') {
						// Only the expired account goes; the others stay signed in.
						store.dispatch(removeAccount(target.username));
						if (target.wasActive) {
							await flushPersist();
							window.location.href = '/home';
						}
					} else if (target.action === 'logoutAll') {
						store.dispatch(userLogout());
						await flushPersist();
						window.location.href = '/login';
					}
					// 'ignore': a stale token that belongs to no signed-in account.
				}
			} catch (err) {
				console.error('401 interceptor: error handling logout', err);
			} finally {
				_handling401 = false;
			}
		}

		return Promise.reject(error);
	}
);

class SublyApi {
	static async request(endpoint, data = {}, method = 'get', header) {
		console.debug('API Call:', endpoint, data, method);
		const url = `${BASE_URL}/${endpoint}`;
		const headers = header || {};
		const params = method === 'get' ? data : {};

		try {
			const res = await apiClient({ url, method, data, params, headers });
			return res ? res.data : 'There is no return response for this route';
		} catch (err) {
			console.error('API Error:', err.response);
			let message = err.response?.data?.error?.message;

			if (message) {
				throw Array.isArray(message) ? message : [message];
			} else {
				throw err.response?.data?.message;
			}
		}
	}

	/** NOTE: The entire user object is
	 *
	 * user: { username, password, firstName, lastName, email, verified, customerId, phone, designations, certifications, specialties
	 *
	 * officeAddressOne, officeAddressTwo, officeCity, officeState, officeZip, mlsInfo, websiteUrl, googleBusinessUrl
	 *
	 * crmName, crmUsername, crmPassword, logo, headshot, brokerName, brokerAddressONe, brokerAddressTwo, brokerCity, brokerState,
	 *
	 * brokerZip, brokerLicence, brokerEmail, serviceAreas, uniqueness, favoritePlaces, travelDestinations, hobbies, otherDetails}
	 *
	 */

	/******************************************************
	 * USERS AUTH API ROUTES
	 *******************************************************/

	/** POST /auth/userRegister:   { data } => { token, user }
	 *
	 * user data must include { username, password, firstName, lastName, email } or else you will receive bad request error
	 *
	 * (min, max) length restrictions ==> {email: (6, 30), username: (5, 20), password: (8,30), firstName & LastName: (2,20) ;
	 *
	 * email must be in email format
	 *
	 * Note: specific error messages come from JSONSCHEMA validation
	 *
	 * Authorization required: none
	 *
	 * @param data {Object} { username, password, firstName, lastName, email }
	 * @returns {Object} {token, user (minus password)}
	 */

	static async userSignup(data) {
		let res = await this.request(`auth/userRegister`, data, 'post');
		return res;
	}

	/** POST /auth/userToken:  { username, password } => { token, user }
	 *
	 * user Login
	 *
	 * Authorization required: none
	 *
	 * @param data {Object} {username, password}
	 * @returns {object} {token}
	 */

	static async userLogin(data) {
		let res = await this.request(`auth/userToken`, data, 'post');
		return res;
	}

	static async sendWelcomeEmail({ username, token }) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`auth/welcomeEmail`,
			{ username },
			'post',
			header
		);
		return res;
	}

	static async sendNewUserEmail({ token }) {
		console.log('this is the token', token);
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(`auth/adminEmail`, {}, 'post', header);
		return res;
	}

	/******************************************************
	 * Mailer API ROUTES
	 *******************************************************/

	/** POST /mailer/add-contact
	 *
	 * Adds a new contact to the mailer list
	 *
	 * Authorization required: none
	 *
	 * @param data {Object} {firstName, lastName, email}
	 * @returns {object} {message, bigmailer}
	 */

	static async addNewContactToMailer({ data }) {
		let res = await this.request(`mailer/add-contact`, data, 'post');
		return res;
	}

	/******************************************************
	 * USERS API ROUTES
	 *******************************************************/

	/** POST / { user }  => { user, token }
	 *
	 * Adds a new user. This is not the user registration endpoint --- instead, this is
	 * only for admin users to add new users.
	 *
	 * This returns the newly created user and an authentication token for them:
	 *  {user , token }
	 *
	 * Authorization required: approved admin
	 **/

	static async adminUserSignup({ data, token }) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(`users`, data, 'post', header);
		return res;
	}

	/** GET / => { users: [ {username, firstName, lastName, email, verified... }, ... ] }
	 *
	 * Returns list of all users.
	 *
	 * Authorization required: approved admin
	 *
	 * @param token {string}
	 * @returns {object} {users}
	 **/

	static async getUsers(token, status = 'active') {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(`users`, { status }, undefined, header);
		return res;
	}

	/** GET /[username] => { user, token }
	 *
	 * get a particular user using username
	 *
	 * Authorization required: approved admin or same user-as-:username
	 *
	 * !Note: we get token. May not be necessary
	 *
	 * @param token {string}
	 * @param username {string}
	 * @returns {object} {token, user (minus password)}
	 **/

	static async getUser(username, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`users/${username}`,
			undefined,
			undefined,
			header
		);
		return res;
	}

	/** PATCH /[username] { user } => { user } or {user, token}
	 *
	 * User input data can include:
	 *  { user (minus username)}
	 *
	 * Authorization required: Approved admin or same-user-as-:username
	 *
	 * @param token {String}
	 * @param username {String}
	 * @param data {Object} can include all user fields except for username.
	 * @returns {object} { user (minus password) } OR { user (minus password), token  } if verified status is changed.
	 **/

	static async updateUser(username, data, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(`users/${username}`, data, 'patch', header);
		return res;
	}

	/** PATCH /[username]/password { oldPassword, password } => { 'success' }
	 *
	 *
	 * Authorization required: Approved admin or same-user-as-:username
	 *
	 * @param token {String}
	 * @param username {String}
	 * @param data {Object} includes only: {oldPassword, password}.
	 * @returns {String} {message: 'success!' }
	 **/

	static async changePassword(username, data, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`users/${username}/password`,
			data,
			'patch',
			header
		);
		return res;
	}

	/** /** POST /[username]/forgot-password  => { contact_id } (from Bigmailer)
	 *
	 * Route for sending forgot password email to user
	 *
	 * Authorization not required
	 *
	 * @param {*} username
	 * @returns {object} {contact_id}(from bigMailer)
	 */

	static async forgotPassword(username) {
		let res = await this.request(
			`users/${username}/forgot-password`,
			undefined,
			'post'
		);
		return res;
	}

	/** PATCH /[username] { data} => { user }
	 *
	 * Function for api call to update images
	 *
	 * @param username {String}
	 * @param  data {Object} FormData object. expects a single field of type Blob for an image.
	 * @param  token {String}
	 * @returns {Object} {user (minus password)}
	 */

	static async updateImages(username, data, token) {
		const header = {
			'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
			Authorization: `Bearer ${token}`,
		};
		let res = await this.request(
			`users/images/${username}`,
			data,
			'patch',
			header
		);
		return res;
	}

	/******************************************************
	 * USER IMAGES API ROUTES (New Multi-Slot System)
	 *******************************************************/

	/** GET /api/user-images/{username} => { images: [...] }
	 *
	 * Get all images for a user, optionally filtered by type
	 *
	 * Authorization required: correct user or admin
	 *
	 * @param username {string}
	 * @param imageType {string} - optional filter by image type
	 * @param organized {boolean} - return organized by type and slot
	 * @param token {string}
	 * @returns {object} { images: [...] }
	 */
	static async getUserImages(username, { imageType, organized } = {}, token) {
		const header = { Authorization: `Bearer ${token}` };
		const params = {};
		if (imageType) params.type = imageType;
		if (organized) params.organized = 'true';
		
		// CACHE BUSTING: Add timestamp to prevent 304 "Not Modified" responses
		// This ensures we always get fresh image data after upload/delete operations
		params._bust = Date.now();

		let res = await this.request(
			`api/user-images/${username}`,
			params,
			'get',
			header
		);
		return res;
	}

	/** POST /api/user-images/{username}/upload => { image: {...} }
	 *
	 * Upload a new image for a user
	 *
	 * Authorization required: correct user or admin
	 *
	 * @param username {string}
	 * @param file {File}
	 * @param imageType {string} - headshot, logo, broker_logo, lifestyle
	 * @param imageSlot {number} - optional slot number
	 * @param caption {string} - optional caption
	 * @param isPrimary {boolean} - optional primary flag
	 * @param token {string}
	 * @returns {object} { image: {...} }
	 */
	static async uploadUserImage(
		username,
		{ file, imageType, imageSlot, caption, isPrimary },
		token
	) {
		const formData = new FormData();
		formData.append('image', file);
		formData.append('imageType', imageType);
		if (imageSlot !== undefined && imageSlot !== null)
			formData.append('imageSlot', imageSlot.toString());
		if (caption) formData.append('caption', caption);
		if (isPrimary !== undefined && isPrimary !== null)
			formData.append('isPrimary', isPrimary.toString());

		const header = { Authorization: `Bearer ${token}` };

		let res = await this.request(
			`api/user-images/${username}/upload`,
			formData,
			'post',
			header
		);
		return res;
	}

	/** GET /api/user-images/{id} => { image: {...} }
	 *
	 * Get specific image by ID
	 *
	 * Authorization required: correct user or admin
	 *
	 * @param imageId {number}
	 * @param token {string}
	 * @returns {object} { image: {...} }
	 */
	static async getUserImage(imageId, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`api/user-images/${imageId}`,
			{},
			'get',
			header
		);
		return res;
	}

	/** PATCH /api/user-images/{id} => { image: {...} }
	 *
	 * Update image metadata (caption, isPrimary)
	 *
	 * Authorization required: correct user or admin
	 *
	 * @param imageId {number}
	 * @param data {object} - { caption, isPrimary }
	 * @param token {string}
	 * @returns {object} { image: {...} }
	 */
	static async updateUserImage(imageId, data, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`api/user-images/${imageId}`,
			data,
			'patch',
			header
		);
		return res;
	}

	/** PUT /api/user-images/{id}/replace => { image: {...} }
	 *
	 * Replace existing image file
	 *
	 * Authorization required: correct user or admin
	 *
	 * @param imageId {number}
	 * @param file {File}
	 * @param token {string}
	 * @returns {object} { image: {...} }
	 */
	static async replaceUserImage(imageId, file, token) {
		const formData = new FormData();
		formData.append('image', file);

		const header = { Authorization: `Bearer ${token}` };

		let res = await this.request(
			`api/user-images/${imageId}/replace`,
			formData,
			'put',
			header
		);
		return res;
	}

	/** DELETE /api/user-images/{id} => { message: "..." }
	 *
	 * Delete image by ID
	 *
	 * Authorization required: correct user or admin
	 *
	 * @param imageId {number}
	 * @param token {string}
	 * @returns {object} { message: "..." }
	 */
	static async deleteUserImage(imageId, token) {
		const header = { Authorization: `Bearer ${token}` };

		let res = await this.request(
			`api/user-images/${imageId}`,
			{},
			'delete',
			header
		);

		return res;
	}

	/** GET /api/user-images/{username}/slots/{imageType} => { nextSlot: number }
	 *
	 * Get next available slot for image type
	 *
	 * Authorization required: correct user or admin
	 *
	 * @param username {string}
	 * @param imageType {string}
	 * @param token {string}
	 * @returns {object} { nextSlot: number }
	 */
	static async getNextAvailableSlot(username, imageType, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`api/user-images/${username}/slots/${imageType}`,
			{},
			'get',
			header
		);
		return res;
	}

	/******************************************************
	 * Admin API ROUTES
	 *******************************************************/

	/** sign admin up and get token  */
	/** POST /auth/adminRegister:   { data } => { token, admin }
	 *
	 * user data must include { username, password, firstName, lastName, email } or else you will receive bad request error
	 *
	 * (min, max) length restrictions ==> {email: (6, 30), username: (5, 20), password: (8,30), firstName & LastName: (2,20) ;
	 *
	 * email must be in email format
	 *
	 * Note: specific error messages come from JSONSCHEMA validation
	 *
	 * Authorization required: none
	 *
	 * @param data {Object} { username, password, firstName, lastName, email }
	 * @returns {Object} {token, { username, firstName, lastName, email, isApproved, imageUrl }}
	 */

	static async adminSignup(data) {
		let res = await this.request(`auth/adminRegister`, data, 'post');
		return res;
	}

	/** log admin in and get token  */
	static async adminLogin(data) {
		let res = await this.request(`auth/adminToken`, data, 'post');
		return res;
	}

	/** GET /[username] => { admin }
	 *
	 * get a particular admin using username
	 *
	 * Authorization required: approved admin
	 *
	 * @param token {string}
	 * @param username {string}
	 * @returns {object} {token, { username, first_name, last_name, email, isApproved, image_URL}}
	 **/

	static async getAdmin(username, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`admins/${username}`,
			undefined,
			undefined,
			header
		);
		return res;
	}

	/******************************************************
	 * Admin Impersonation API ROUTES
	 *******************************************************/

	/** POST /admins/impersonate/:username  => { token, user, impersonationInfo }
	 *
	 * Impersonate a user as an admin
	 *
	 * Authorization required: approved admin
	 *
	 * @param username {string} - Username to impersonate
	 * @param token {string} - Admin token
	 * @returns {object} { token, user, impersonationInfo }
	 */
	static async impersonateUser(username, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`admins/impersonate/${username}`,
			{},
			'post',
			header
		);
		return res;
	}

	/** POST /admins/impersonate/end  => { message }
	 *
	 * End impersonation session (notifies backend for audit logging)
	 *
	 * Authorization required: approved admin
	 *
	 * @param data {object} - { targetUsername, reason }
	 * @param token {string} - Admin token
	 * @returns {object} { message }
	 */
	static async endImpersonation(data, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request('admins/impersonate/end', data, 'post', header);
		return res;
	}

	/******************************************************
	 * Stripe API ROUTES
	 * Currently, products are stored on Stripe API
	 *******************************************************/

	/** Get a list of all pricess from stripe, accounting for filtering data
	 * filtering data options can be found here https://stripe.com/docs/api/prices/list?lang=node
	 */
	static async getStripePrices(token, data = {}) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(`stripe/get-prices`, data, undefined, header);
		return res;
	}

	/** Retrieves product subscriptions from the stripe API for each user.  Returns {stripeSubs: [array of stripe price objects]} */
	static async getStripeSubscriptions(customerId, token) {
		console.log('we are getting stripe subs');
		const header = { Authorization: `Bearer ${token}` };
		const data = { customerId };
		let res = await this.request(`stripe/get-user-subs`, data, 'post', header);
		return res;
	}
	/** Retrieves product subscriptions from the stripe API for each user.  Returns {stripeSubs: [array of stripe price objects]} */
	static async getAllStripeSubscriptions(token) {
		const header = { Authorization: `Bearer ${token}` };
		
		// CACHE BUSTING: Add timestamp to prevent 304 "Not Modified" responses
		// This ensures we always get fresh subscription data after new subscriptions
		const params = { _bust: Date.now() };
		
		let res = await this.request(`stripe/get-all-subs`, params, 'get', header);
		return res;
	}
	/** Retrieves subscription events from Stripe for historical MRR calculation. Returns {events: [array of subscription events]} */
	static async getSubscriptionEvents(token, days = 30) {
		const header = { Authorization: `Bearer ${token}` };
		
		// CACHE BUSTING: Add timestamp to prevent 304 "Not Modified" responses
		// This ensures we always get fresh subscription events data
		const params = { days, _bust: Date.now() };
		
		let res = await this.request(`stripe/get-subscription-events`, params, 'get', header);
		return res;
	}
	
	/** Retries payment for a failed invoice. Returns {success, invoice} */
	static async retryPayment(token, invoiceId) {
		const header = { Authorization: `Bearer ${token}` };
		
		let res = await this.request(`stripe/retry-payment/${invoiceId}`, {}, 'post', header);
		return res;
	}
	
	/** Cancels a subscription. Returns {success, subscription} */
	static async cancelSubscription(token, subscriptionId, cancelAtPeriodEnd = false) {
		const header = { Authorization: `Bearer ${token}` };
		const data = { cancelAtPeriodEnd };
		
		let res = await this.request(`stripe/cancel-subscription/${subscriptionId}`, data, 'post', header);
		return res;
	}

	/** creates a new stripe customer given email, and name */
	static async createCustomer(email, name, token) {
		const header = { Authorization: `Bearer ${token}` };
		const data = { email, name };
		let res = await this.request(
			`stripe/create-customer`,
			data,
			'post',
			header
		);
		return res.customerId;
	}

	/** forwards user to customer portal */
	static async customerPortal(customerId, token, returnUrl = null) {
		console.log('hi there');
		const header = { Authorization: `Bearer ${token}` };
		const data = { customerId };

		// Add returnUrl to the request if provided
		if (returnUrl) {
			data.returnUrl = returnUrl;
		}

		let res = await this.request(
			`stripe/customer-portal`,
			data,
			'post',
			header
		);
		return res;
	}

	/** creates checkout session
	 * data= {priceId, customerId}
	 */
	static async createCheckoutSession(data, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`stripe/create-checkout-session`,
			{ ...data, origin: window.location.origin },
			'post',
			header
		);
		return res;
	}

	/******************************************************
	 * Stripe API ROUTES
	 * Currently, products are stored on Stripe API
	 *******************************************************/
	/** Get a list of all user's responses from typeform for a particular form,
	 * data = {formId}
	 * see responses at typeform dev api here: https://www.typeform.com/developers/responses/
	 */
	static async getTypeformResponses({ username, formId, token }) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`typeform/get-responses/${username}`,
			{ formId },
			undefined,
			header
		);
		return res;
	}

	/******************************************************
	 * Forms API ROUTES
	 *******************************************************/

	/** Get user-accessible forms based on subscription lookup keys */
	static async getUserAccessibleForms(token, params = {}) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`api/forms/user-accessible`,
			params,
			'get',
			header
		);
		return res;
	}

	/** Get user's subscription lookup keys */
	static async getUserLookupKeys(token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`api/forms/subscription-lookup-keys`,
			{},
			'get',
			header
		);
		return res;
	}

	/** Get form submissions for a user */
	static async getFormSubmissions(username, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`api/forms/submissions/user/${username}`,
			{},
			'get',
			header
		);
		return res;
	}

	/** Update a form submission */
	static async updateFormSubmission(submissionId, data, token) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`api/forms/submissions/${submissionId}`,
			data,
			'put',
			header
		);
		return res;
	}

	/** Get admin form submissions with date filtering for activity analysis */
	static async getAdminFormSubmissions(token, { startDate, endDate, forceRefresh = false, ...otherParams } = {}) {
		const header = { Authorization: `Bearer ${token}` };
		
		const params = { ...otherParams };
		
		if (startDate) {
			params.start_date = startDate;
		}
		if (endDate) {
			params.end_date = endDate;
		}
		
		// Import cache manager dynamically to avoid circular dependencies
		const { cachedApiRequest, apiCache } = await import('../utils/cacheManager');
		
		// Generate cache key
		const cacheKey = apiCache.generateKey('api/admin/forms/submissions', params);
		
		// API function to cache
		const apiCall = async () => {
			// Add cache busting only when forcing refresh or cache miss
			const requestParams = forceRefresh ? { ...params, _bust: Date.now() } : params;
			const queryParams = new URLSearchParams(requestParams);
			
			return await this.request(
				`api/admin/forms/submissions?${queryParams}`,
				{},
				'get',
				header
			);
		};
		
		// Use cached request with smart invalidation
		return await cachedApiRequest(apiCall, cacheKey, { 
			forceRefresh,
			ttl: 5 * 60 * 1000 // 5 minutes for submission data
		});
	}

	/** Get lightweight submission count for real-time polling (performance optimized) */
	static async getSubmissionCount(token, { startDate, endDate } = {}) {
		const header = { Authorization: `Bearer ${token}` };
		
		const params = { count_only: 'true' };
		
		if (startDate) {
			params.start_date = startDate;
		}
		if (endDate) {
			params.end_date = endDate;
		}
		
		// Don't add cache busting for count checks - let the browser cache work
		// This endpoint is specifically designed for lightweight polling
		
		const queryParams = new URLSearchParams(params);
		
		try {
			let res = await this.request(
				`api/admin/forms/submissions/count?${queryParams}`,
				{},
				'get',
				header
			);
			return res;
		} catch (err) {
			// Fallback to regular endpoint if count endpoint doesn't exist
			if (err?.response?.status === 404) {
				const response = await this.request(
					`api/admin/forms/submissions?${queryParams}`,
					{},
					'get',
					header
				);
				const submissions = response?.submissions || response || [];
				return { count: submissions.length };
			}
			throw err;
		}
	}

	/** Get submission breakdown by form name for dashboard card (optimized for pending status) */
	static async getSubmissionFormBreakdown(token, { status = 'pending', forceRefresh = false } = {}) {
		const header = { Authorization: `Bearer ${token}` };
		
		// For 'pending' status, use the special pending parameter that the backend supports
		const params = {};
		if (status === 'pending') {
			params.pending = 'true';
		} else if (status) {
			params.status = status;
		}
		
		// Import cache manager dynamically to avoid circular dependencies
		const { cachedApiRequest, apiCache } = await import('../utils/cacheManager');
		
		// Generate cache key
		const cacheKey = apiCache.generateKey('api/admin/forms/submissions/breakdown', { status });
		
		// API function to cache
		const apiCall = async () => {
			// Add cache busting only when forcing refresh
			const requestParams = forceRefresh ? { ...params, _bust: Date.now() } : params;
			
			// For breakdown counting, we need ALL pending submissions, not just paginated results
			requestParams.limit = 9999; // High limit to get all submissions for accurate count
			
			const queryParams = new URLSearchParams(requestParams);
			
			const response = await this.request(
				`api/admin/forms/submissions?${queryParams}`,
				{},
				'get',
				header
			);
			
			// Process the response to group by form name
			const submissions = response?.submissions || response || [];
			const breakdown = {};
			
			submissions.forEach(submission => {
				const formName = submission.form_name || 'Unknown Form';
				breakdown[formName] = (breakdown[formName] || 0) + 1;
			});
			
			// Convert to array format with consistent color assignment
			const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-gray-500'];
			const result = Object.entries(breakdown)
				.map(([formName, count], index) => ({
					type: formName,
					count,
					color: colors[index % colors.length]
				}))
				.sort((a, b) => b.count - a.count); // Sort by count descending
			
			return result;
		};
		
		// Use cached request with smart invalidation
		return await cachedApiRequest(apiCall, cacheKey, { 
			forceRefresh,
			ttl: 2 * 60 * 1000 // 2 minutes for breakdown data (shorter TTL for dashboard)
		});
	}

	/******************************************************
	 * Product API ROUTES
	 * Currently, these routes are unused, since we are not storing product data
	 * !This is Depreciated
	 *******************************************************/

	/** Get a list of all products, accounting for filtering data  (max price, description, title) */
	static async getProducts(token, data = {}) {
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(`products`, data, undefined, header);
		return res.products;
	}

	/** Applies to product given username and product id params */
	static async subscribeToProduct(username, productId, token) {
		console.log('we are attempting to subscribe');
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`users/${username}/products/${productId}`,
			undefined,
			'post',
			header
		);
		return res.subcribed;
	}

	/** Applies to product given username and product id params */
	static async unsubscribeFromProduct(username, productId, token) {
		console.log('we are attempting to unsubscribe');
		const header = { Authorization: `Bearer ${token}` };
		let res = await this.request(
			`users/${username}/products/${productId}`,
			undefined,
			'delete',
			header
		);
		return res.unsubcribed;
	}

	// ========== Expense Methods (Spending Tracker) ==========

	/**
	 * Get expenses for logged-in user (client portal)
	 * @param {Object} filters - { start_date, end_date, category }
	 * @returns {Promise<Object>} Response with expenses array in camelCase
	 */
	static async getExpenses(filters = {}) {
		const queryString = new URLSearchParams(filters).toString();
		const url = queryString ? `api/expenses?${queryString}` : 'api/expenses';
		return await this.request(url);
	}

	/**
	 * Get single expense by ID
	 * @param {number} id - Expense ID
	 * @returns {Promise<Object>} Expense in camelCase
	 */
	static async getExpense(id) {
		return await this.request(`api/expenses/${id}`);
	}

	/**
	 * Create new expense
	 * @param {Object} data - Expense data in snake_case
	 *   { name, category, amount, expense_date, notes }
	 * @returns {Promise<Object>} Created expense in camelCase
	 */
	static async createExpense(data) {
		return await this.request('api/expenses', data, 'post');
	}

	/**
	 * Update expense
	 * @param {number} id - Expense ID
	 * @param {Object} data - Updated fields in snake_case
	 * @returns {Promise<Object>} Updated expense in camelCase
	 */
	static async updateExpense(id, data) {
		return await this.request(`api/expenses/${id}`, data, 'patch');
	}

	/**
	 * Delete expense
	 * @param {number} id - Expense ID
	 * @returns {Promise<Object>} Deletion confirmation
	 */
	static async deleteExpense(id) {
		return await this.request(`api/expenses/${id}`, {}, 'delete');
	}

	/**
	 * Get expense summary (totals and category breakdown)
	 * @param {Object} filters - { start_date, end_date }
	 * @returns {Promise<Object>} Summary with totalAmount, totalCount, byCategory
	 */
	static async getExpenseSummary(filters = {}) {
		const queryString = new URLSearchParams(filters).toString();
		const url = queryString
			? `api/expenses/summary?${queryString}`
			: 'api/expenses/summary';
		return await this.request(url);
	}

	// ========== Admin Expense Methods ==========

	/**
	 * Get all expenses (admin only)
	 * @param {Object} filters - { start_date, end_date, category }
	 * @returns {Promise<Object>} All expenses across all users
	 */
	static async getAllExpenses(filters = {}) {
		const queryString = new URLSearchParams(filters).toString();
		const url = queryString
			? `api/admin/expenses?${queryString}`
			: 'api/admin/expenses';
		return await this.request(url);
	}

	/**
	 * Get expenses for specific user (admin only)
	 * @param {string} username - Target user's username
	 * @param {Object} filters - { start_date, end_date, category }
	 * @returns {Promise<Object>} User's expenses
	 */
	static async getUserExpenses(username, filters = {}) {
		const queryString = new URLSearchParams(filters).toString();
		const url = queryString
			? `api/admin/expenses/user/${username}?${queryString}`
			: `api/admin/expenses/user/${username}`;
		return await this.request(url);
	}

	/**
	 * Get aggregated summary across all users (admin only)
	 * @param {Object} filters - { start_date, end_date }
	 * @returns {Promise<Object>} Aggregated summary
	 */
	static async getAdminExpenseSummary(filters = {}) {
		const queryString = new URLSearchParams(filters).toString();
		const url = queryString
			? `api/admin/expenses/summary?${queryString}`
			: 'api/admin/expenses/summary';
		return await this.request(url);
	}

	/**
	 * Create expense for a user (admin only)
	 * @param {Object} data - Expense data including username
	 *   { username, name, category, amount, expense_date, notes }
	 * @returns {Promise<Object>} Created expense
	 */
	static async createExpenseForUser(data) {
		return await this.request('api/admin/expenses', data, 'post');
	}

	/**
	 * Update any expense (admin only)
	 * @param {number} id - Expense ID
	 * @param {Object} data - Updated fields
	 * @returns {Promise<Object>} Updated expense
	 */
	static async updateExpenseAsAdmin(id, data) {
		return await this.request(`api/admin/expenses/${id}`, data, 'patch');
	}

	/**
	 * Delete any expense (admin only)
	 * @param {number} id - Expense ID
	 * @returns {Promise<Object>} Deletion confirmation
	 */
	static async deleteExpenseAsAdmin(id) {
		return await this.request(`api/admin/expenses/${id}`, {}, 'delete');
	}
}

export default SublyApi;
